const ACTION_LABELS = {
  BILL_CREATED: "Bill created",
  BILL_UPDATED: "Bill updated",
  BILL_REPRINTED: "Bill reprinted",
  BILL_VOIDED: "Bill voided",
  BILL_ITEMS_VOIDED: "Bill items voided",
  BILL_REFUNDED: "Bill refunded",
  BILL_REFUND_PENDING: "Refund pending",
  BILL_REFUND_FAILED: "Refund failed",
  BILL_PAYMENT_CORRECTED: "Payment methods corrected"
};

const FIELD_LABELS = {
  BillStatus: "Bill status", IsPaid: "Paid status", PaymentMode: "Payment mode",
  SubTotal: "Subtotal", PackagingCharge: "Packaging charge", DeliveryCharge: "Delivery charge",
  ServiceChargeAmount: "Service charge", CGSTAmount: "CGST", SGSTAmount: "SGST",
  DiscountAmount: "Discount", GrandTotal: "Grand total", SettlementAmount: "Settlement amount",
  ChangeReturn: "Change returned", OrderMode: "Order mode", Amount: "Amount",
  RefundMode: "Refund mode", RefundStatus: "Refund status", ReferenceNumber: "Reference",
  Items: "Items", Payments: "Payments", BillRefundId: "Refund ID", ApprovedByUserId: "Approved by user ID"
};

const MONEY_FIELDS = new Set([
  "SubTotal", "PackagingCharge", "DeliveryCharge", "ServiceChargeAmount", "CGSTAmount",
  "SGSTAmount", "DiscountAmount", "GrandTotal", "SettlementAmount", "ChangeReturn",
  "Amount", "RefundAmount"
]);

export const parseAuditValues = value => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try { return JSON.parse(value) || {}; } catch { return {}; }
};

const comparable = value => JSON.stringify(value ?? null);

export const auditActionLabel = action => ACTION_LABELS[action] || String(action || "Audit activity")
  .toLowerCase().replace(/_/g, " ").replace(/^./, value => value.toUpperCase());

export const auditFieldLabel = field => FIELD_LABELS[field] || String(field || "Value")
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, value => value.toUpperCase());

export const getAuditChanges = entry => {
  if (entry?.action === "BILL_REPRINTED") return [];
  const previous = parseAuditValues(entry?.previousValuesJson);
  const next = parseAuditValues(entry?.newValuesJson);
  return [...new Set([...Object.keys(previous), ...Object.keys(next)])]
    .filter(field => comparable(previous[field]) !== comparable(next[field]))
    .map(field => ({ field, before: previous[field], after: next[field] }));
};

const itemText = item => {
  const paymentMode = item?.PaymentMode ?? item?.paymentMode;
  if (paymentMode) {
    const amount = item?.Amount ?? item?.amount ?? 0;
    const received = item?.TenderedAmount ?? item?.tenderedAmount ?? amount;
    const reference = item?.ReferenceNumber ?? item?.referenceNumber;
    return `${paymentMode}: ₹${Number(amount).toFixed(2)}${Number(received) !== Number(amount) ? `, received ₹${Number(received).toFixed(2)}` : ""}${reference ? ` (${reference})` : ""}`;
  }
  const name = item?.ItemName ?? item?.Name ?? item?.itemName ?? item?.name ?? "Item";
  const quantity = item?.Quantity ?? item?.quantity;
  const amount = item?.RefundAmount ?? item?.Amount ?? item?.refundAmount ?? item?.amount;
  return `${name}${quantity != null ? ` × ${quantity}` : ""}${amount != null ? ` (₹${Number(amount).toFixed(2)})` : ""}`;
};

export const formatAuditValue = (value, field) => {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.map(itemText).join(", ") : "—";
  if (typeof value === "object") return Object.entries(value)
    .map(([key, nested]) => `${auditFieldLabel(key)}: ${formatAuditValue(nested, key)}`).join(", ");
  if (MONEY_FIELDS.has(field) && !Number.isNaN(Number(value))) return `₹${Number(value).toFixed(2)}`;
  return String(value);
};

export const createAuditExportRows = (audit, translate = value => value) => audit.flatMap(entry => {
  const changes = getAuditChanges(entry);
  const base = [
    new Date(entry.createdAt).toLocaleString(),
    translate(auditActionLabel(entry.action)),
    entry.reprintNumber || "",
    entry.performedByName || (entry.performedByUserId ? `${translate("User")} #${entry.performedByUserId}` : translate("System")),
    entry.approvedByName || (entry.approvedByUserId ? `User #${entry.approvedByUserId}` : ""),
    entry.terminalId || translate("Not recorded"),
    entry.reason || ""
  ];
  if (!changes.length) return [[...base, "", "", ""]];
  return changes.map(change => [...base, translate(auditFieldLabel(change.field)),
    translate(formatAuditValue(change.before, change.field)), translate(formatAuditValue(change.after, change.field))]);
});

